import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const testimonials = [
  {
    quote: "Thanks Jeff! I am extremely please with our results we have gained so far and have garnered additional interest from other companies that have heard of our success using your program.",
    author: "Chris P.",

  },
  {
    quote: "Amber, Thank you so much for your after hours help. You have saved the day once again.",
    author: "Daniel G.",

  },
  {
    quote: "Good morning Ben. Just a quick note to let you know that voting ended Tuesday evening and results were final yesterday. I won re-election to my board position with about 60% of the votes! Hard to know the true effectiveness of any single activity in a campaign, but I would use your voter data service and text messaging service without hesitation, and would recommend it to any of my peers who may need it. You were knowledgeable, responsive, patient, and you delivered as promised on short notice every time I called. Thank you for your service and support — I sincerely believe it was a significant contributor to my success. ",
    author: "Robert S.",
  },
  {
    quote:"Happy turkey week, all! Very thankful for the opportunity to learn from you all here in the wild & wonderful world of P2P :)",
    author: "Danielle S.",
  },
  {
    quote:"Hi Amber & Rachele, Adam and I just wanted to say a huge thank you for the amazing Halloween Goodie Box, you two are simply the best! 🎃 Everything was so thoughtful and fun. As you can see in the second picture, I still have the little spider from last year, he’s my all-year Halloween tradition! We’re so grateful for your friendship and partnership. ",
    author: "Robert F.",
  },
  {
    quote:"Hi Ashley,I hope you had a nice weekend! Thank you so much for digging into this. We really appreciate your help and I know TPM appreciates it too. We&apos;re so grateful for your friendship and partnership. I hope you have a great day, and please don&apos;t hesitate if you need anything.",
    author: "Ryan F.",
  }
];

const About = () => {
  const aboutSectionRef = useRef(null);
  const scrollPathsRef = useRef(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    // Register GSAP plugins
    gsap.registerPlugin(ScrollTrigger);

    // Apply reveal animation to section title
    const sectionTitle = document.querySelector('#about h2');
    if (sectionTitle) {
      sectionTitle.classList.add('reveal');
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      }, {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px'
      });
      revealObserver.observe(sectionTitle);
    }

    // Apply stagger animation to about grid
    const aboutGrid = document.querySelector('[data-about-grid]');
    if (aboutGrid) {
      const items = aboutGrid.querySelectorAll('[data-value-card], [data-mission-card]');
      items.forEach(item => {
        item.classList.add('reveal-stagger');
      });
      const staggerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const group = entry.target;
            const items = group.querySelectorAll('.reveal-stagger');
            items.forEach((item, index) => {
              setTimeout(() => {
                item.classList.add('revealed');
              }, index * 150);
            });
            staggerObserver.unobserve(group);
          }
        });
      }, {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px'
      });
      staggerObserver.observe(aboutGrid);
    }

    // Scroll paths animation - wait for DOM to be ready
    const initScrollPaths = () => {
      if (!aboutSectionRef.current) return;

      const scrollPaths = document.querySelectorAll('.scroll-path');
      
      if (scrollPaths.length === 0) return;

      // Kill any existing ScrollTriggers for this section
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars && trigger.vars.trigger === aboutSectionRef.current) {
          trigger.kill();
        }
      });

      const masterTL = gsap.timeline({
        scrollTrigger: {
          trigger: aboutSectionRef.current,
          start: 'top 80%',
          end: 'bottom 20%',
          scrub: 0.01,
        }
      });

      scrollPaths.forEach((scrollPath, index) => {
        const pathLength = scrollPath.getTotalLength();
        if (pathLength === 0) return; // Skip if path has no length
        
        scrollPath.style.strokeDasharray = pathLength;
        scrollPath.style.strokeDashoffset = pathLength;

        if (index === 0) {
          // Main path - should complete before split paths start
          scrollPath.style.opacity = '1';
          masterTL.to(scrollPath, {
            strokeDashoffset: 0,
            duration: 0.7,
            ease: 'none'
          }, 0);
        } else {
          // Split paths - start after main path completes
          scrollPath.style.opacity = '1';
          masterTL.to(scrollPath, {
            strokeDashoffset: 0,
            duration: 0.9,
            ease: 'none'
          }, 0.7);
        }
      });
    };

    // Clock animation
    const hourHandGroup = document.getElementById('hour-hand-group');
    const minuteHandGroup = document.getElementById('minute-hand-group');
    let clockAnimationInterval = null;
    let clockObserver = null;

    if (hourHandGroup && minuteHandGroup) {

      let currentAngle = {
        hour: 0,
        minute: 0
      };
      let rotationProgress = 0;
      const targetRotations = 3 * 360;
      const fastSpeed = 15;
      let isSpinning = false;
      let lastTickTime = Date.now();
      let hasAnimated = false;

      const updateClock = () => {
        if (isSpinning) {
          currentAngle.minute -= fastSpeed;
          currentAngle.hour -= fastSpeed / 12;
          rotationProgress += fastSpeed;

          if (rotationProgress >= targetRotations) {
            isSpinning = false;
            currentAngle.minute = Math.round(currentAngle.minute / 6) * 6;
            currentAngle.hour = Math.round(currentAngle.hour / 30) * 30;
          }
        } else {
          const now = Date.now();
          if (now - lastTickTime >= 1000) {
            currentAngle.minute += 6;
            currentAngle.hour += 0.5;
            lastTickTime = now;
          }
        }

        if (hourHandGroup) {
          hourHandGroup.setAttribute('transform', `translate(280, 280) rotate(${currentAngle.hour})`);
        }
        if (minuteHandGroup) {
          minuteHandGroup.setAttribute('transform', `translate(280, 280) rotate(${currentAngle.minute})`);
        }
      };

      const startClockAnimation = () => {
        if (hasAnimated) return;
        hasAnimated = true;
        isSpinning = true;
        rotationProgress = 0;
      };

      clockObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasAnimated) {
            startClockAnimation();
          }
        });
      }, { threshold: 0.1 });

      const aboutSection = aboutSectionRef.current;
      if (aboutSection) {
        clockObserver.observe(aboutSection);
      }

      clockAnimationInterval = setInterval(updateClock, 16);
    }

    // Initialize scroll paths after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      initScrollPaths();
    }, 100);

    // Capture ref value for cleanup
    const aboutSectionElement = aboutSectionRef.current;

    return () => {
      clearTimeout(timeoutId);
      // Cleanup ScrollTriggers
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars && trigger.vars.trigger === aboutSectionElement) {
          trigger.kill();
        }
      });
      // Cleanup clock animation
      if (clockAnimationInterval) {
        clearInterval(clockAnimationInterval);
      }
      if (clockObserver) {
        clockObserver.disconnect();
      }
    };
  }, []);

  // Testimonial auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="about"
      ref={aboutSectionRef}
      className="py-12 md:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 min-h-[120vh] relative z-[2] bg-[var(--bg-white)] overflow-hidden w-full max-w-[100vw]"
      style={{
        backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(122, 91, 109, 0.02) 2px, rgba(122, 91, 109, 0.02) 4px),
          repeating-linear-gradient(-45deg, transparent, transparent 2px, rgba(79, 49, 61, 0.015) 2px, rgba(79, 49, 61, 0.015) 4px)
        `,
        backgroundSize: '40px 40px'
      }}
    >
      {/* Scroll paths SVG - background decoration */}
      <div
        ref={scrollPathsRef}
        className="absolute -top-[50px] left-[calc(-5%-40px)] w-[100%] h-[2000px] overflow-visible z-[1] pointer-events-none"
      >
        <svg className="w-full h-full block overflow-visible" viewBox="-400 0 1400 2000" preserveAspectRatio="none">
          {/* Main wavy scroll path - flows from top center, curves down and splits */}
          <path
            className="scroll-path scroll-path-main"
            d="M500,0 Q470,200 470,400 Q470,600 540,680 Q620,770 760,755 Q900,740 990,615 Q1080,490 1050,315 Q1020,140 900,100 C850,80 700,100 650,150 C600,200 550,280 520,400 C490,520 480,700 490,900 C500,1100 480,1200 460,1280 C445,1330 430,1365 420,1380 L420,1400"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="20"
            strokeLinecap="round"
          />
          {/* Split path 1 - far left */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1400
               L -100 1500
               C -100 1550, -100 1600, -100 1650
               C -100 1700, -100 1750, -100 1800
               L -100 1820"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 2 - left */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1400
               L 150 1500
               C 150 1550, 150 1600, 150 1650
               C 150 1700, 150 1750, 150 1800
               L 150 1820"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 3 - center */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1400
               L 420 1500
               C 420 1550, 420 1600, 420 1650
               C 420 1700, 420 1750, 420 1800
               L 420 1820"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 4 - right */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1400
               L 680 1500
               C 680 1550, 680 1600, 680 1650
               C 680 1700, 680 1750, 680 1800
               L 680 1820"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 5 - far right */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1400
               L 920 1500
               C 920 1550, 920 1600, 920 1650
               C 920 1700, 920 1750, 920 1800
               L 920 1820"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Main container with max-width for consistent layout */}
      <div className="max-w-[1600px] mx-auto relative overflow-visible w-full">
        {/* Clock Container - positioned at top right of about section */}
        <div className="hidden lg:block absolute top-8 right-[5%] xl:right-[8%] 2xl:right-[8%] z-[10] p-2 sm:p-3 md:p-4 lg:p-5">
          <svg
            className="block drop-shadow-[0_8px_16px_rgba(0,0,0,0.3)] overflow-hidden w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] md:w-[420px] md:h-[420px]"
            viewBox="0 0 560 560"
          >
            <defs>
              <filter id="clock-shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="rgba(0,0,0,0.5)"/>
                <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="rgba(0,0,0,0.3)"/>
              </filter>
              <linearGradient id="clock-ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2a2a2a" />
                <stop offset="50%" stopColor="#1a1a1a" />
                <stop offset="100%" stopColor="#0a0a0a" />
              </linearGradient>
            </defs>
            {/* Clock face - bigger and 3D */}
            <circle
              cx="280"
              cy="280"
              r="240"
              fill="none"
              stroke="url(#clock-ring-gradient)"
              strokeWidth="12"
              filter="url(#clock-shadow)"
            />
            {/* Inner highlight for 3D effect */}
            <circle
              cx="280"
              cy="280"
              r="240"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="2"
              strokeDasharray="20 10"
            />
            {/* Hour markers */}
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((hour) => {
              const angle = (hour * 30 - 90) * (Math.PI / 180);
              const x1 = 280 + 200 * Math.cos(angle);
              const y1 = 280 + 200 * Math.sin(angle);
              const x2 = 280 + 220 * Math.cos(angle);
              const y2 = 280 + 220 * Math.sin(angle);
              return (
                <line
                  key={hour}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="#1a1a1a"
                  strokeWidth="6"
                />
              );
            })}
            {/* Hour hand - centered at 280, 280 */}
            <g id="hour-hand-group" transform="translate(280, 280)">
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="-100"
                stroke="#9a83ad"
                strokeWidth="8"
                strokeLinecap="round"
              />
            </g>
            {/* Minute hand - centered at 280, 280 */}
            <g id="minute-hand-group" transform="translate(280, 280)">
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="-160"
                stroke="#666666"
                strokeWidth="6"
                strokeLinecap="round"
              />
            </g>
            {/* Center dot */}
            <circle cx="280" cy="280" r="12" fill="#9a83ad" />
          </svg>
        </div>

        {/* Section Title - aligned left */}
        <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[8rem] mb-8 md:mb-12 text-left border-b-0 pb-0 relative font-serif text-black max-w-full overflow-visible whitespace-nowrap after:content-[''] after:absolute after:-bottom-[10px] after:left-0 after:w-[150px] sm:w-[200px] md:w-[250px] after:h-[4px] sm:after:h-[5px] md:after:h-[6px] after:bg-[var(--accent-color)] after:transform-none">
          About Us
        </h2>

        {/* Our Story Card */}
        <div className="bg-[var(--bg-dark)] p-6 sm:p-8 md:p-10 lg:p-12 mb-8 md:mb-12 lg:mb-16 border border-[var(--border-dark)] shadow-md w-full md:w-[60%] lg:w-1/2 max-w-[700px] rounded-xl relative">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold uppercase tracking-[2px] mb-6 md:mb-8 text-[var(--bg-pure-white)] font-sans border-b-2 border-[var(--accent-color)] pb-3 md:pb-4">
            Our Story
          </h3>
          <div className="text-[var(--bg-pure-white)] leading-[1.8] text-sm sm:text-base md:text-[1.05rem] font-sans">
            <p className="mb-4 md:mb-6">
            For nearly four decades, The VBC Group has partnered with organizations doing impactful work to ensure their messages reach 
            the people who matter most. Built on a foundation of trusted outreach services dating back to 1987, we bring together experience, 
            innovation, and human-centered communication under one unified brand.
            </p>
            <p className="mb-4 md:mb-6">
            What sets The VBC Group apart is our belief that 
            <span className="text-[var(--accent-color)] font-semibold inline leading-[1.8]"> effective communication is both powerful and personal. </span> 
            We don't just deliver messages — we help our clients ask better questions, hear real answers, and turn insight into action. By combining proven technology 
            with hands-on partnership, we make it easier to reach audiences clearly, gather meaningful feedback, and drive measurable results at scale.
            </p>
            <p className="mb-4 md:mb-6">
            We've supported millions of outreach efforts and delivered billions of communications. But our focus has never been just volume. It's about 
            clarity. 
            <span className="text-[var(--accent-color)] font-semibold inline leading-[1.8]"> It's about trust.</span>
            </p>

            <p className="mb-0">
            Today, The VBC Group serves as a strategic communications partner for organizations that need reliability, responsiveness, and results — all backed by a team 
            that understands the responsibility that comes with amplifying a voice.
            </p>
          </div>
        </div>

        {/* Mission section */}
        <div className="relative flex flex-col lg:flex-row items-start justify-start gap-8 lg:gap-12 mb-0">
          {/* Our Mission Card - positioned on the left */}
          <div className="w-full lg:w-[50%] max-w-[700px] z-[10] bg-transparent p-0 border-0 shadow-none text-left mt-8 lg:mt-0 mr-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[5rem] font-bold uppercase tracking-[2px] sm:tracking-[3px] md:tracking-[4px] mb-4 sm:mb-6 md:mb-8 text-[var(--text-dark)] font-sans border-b-0 pb-0 leading-[1.2] max-w-full">
              OUR MISSION
            </h2>
            <p className="text-base sm:text-lg md:text-xl lg:text-xl text-[var(--text-dark)] leading-[1.5] sm:leading-[1.6] font-sans m-0 font-medium w-full lg:w-[85%]">
              We empower organizations doing impactful work by connecting them with
              their audiences through <strong className="text-[var(--accent-color)] font-bold">reliable and responsive</strong> outreach solutions.
            </p>
          </div>
        </div>

        {/* Our Customer Reviews Title */}
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-[2px] sm:tracking-[3px] md:tracking-[4px] text-right text-[var(--text-dark)] mb-8 sm:mb-10 md:mb-12 font-sans relative z-[10] ml-auto w-full md:w-[50%] lg:w-[40%] max-w-[600px] whitespace-nowrap -mt-[340px] md:-mt-[400px] lg:-mt-[460px]">
          OUR CUSTOMER REVIEWS
        </h2>

        {/* Testimonial Slideshow */}
        <div className="mb-20 md:mb-24 lg:mb-32 w-full md:w-[50%] lg:w-[40%] max-w-[580px] relative flex flex-col items-center gap-4 ml-auto mt-0">
          <div className="w-full relative overflow-hidden h-[380px]">
            <div 
              className="absolute inset-0 flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentTestimonial * 100}%)`
              }}
            >
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="min-w-full h-full px-0"
                >
                  <div
                    className="bg-white border-4 border-[#9a83ad] rounded-xl p-5 md:p-6 shadow-lg relative overflow-hidden h-[380px] flex flex-col"
                    style={{
                      backgroundImage: `
                        repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(223, 192, 181, 0.03) 1px, rgba(223, 192, 181, 0.03) 2px)
                      `,
                      backgroundSize: '20px 20px'
                    }}
                  >
                    <div className="absolute top-3 left-3 text-4xl text-[#9a83ad] opacity-20 font-serif">&ldquo;</div>
                    <div className="relative z-10 flex-1 flex flex-col overflow-hidden items-center">
                      <div className="flex-1 flex items-center justify-center">
                        <p className="text-[var(--text-dark)] text-sm md:text-base leading-relaxed font-serif overflow-y-auto text-center px-4">
                          <span className="text-[#9a83ad] text-xl font-serif">&ldquo;</span>
                          {testimonial.quote}
                          <span className="text-[#9a83ad] text-xl font-serif">&rdquo;</span>
                        </p>
                      </div>
                      <div className="border-t-2 border-[#9a83ad] pt-2 pb-0 text-center flex-shrink-0 w-full">
                        <p className="font-semibold text-[var(--text-dark)] text-sm mb-0.5">
                          {testimonial.author}
                        </p>
                        {testimonial.role && (
                          <p className="text-xs text-gray-600 mb-0.5">
                            {testimonial.role}
                          </p>
                        )}
                        {testimonial.organization && (
                          <p className="text-xs text-[#9a83ad] font-semibold mb-0">
                            {testimonial.organization}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation buttons below card */}
          <div className="flex gap-4 mt-2 justify-center w-full">
            <button
              onClick={prevTestimonial}
              className="w-10 h-10 rounded-full bg-[#9a83ad] text-white flex items-center justify-center hover:bg-[#dfc0b5] transition-colors"
              aria-label="Previous testimonial"
            >
              ←
            </button>
            <button
              onClick={nextTestimonial}
              className="w-10 h-10 rounded-full bg-[#9a83ad] text-white flex items-center justify-center hover:bg-[#dfc0b5] transition-colors"
              aria-label="Next testimonial"
            >
              →
            </button>
          </div>
        </div>

        {/* Our Values Title */}
        <div className="mt-12 sm:mt-16 md:mt-20 lg:mt-24 xl:mt-28 mx-auto w-[90%] max-w-[1400px] relative z-[10] flex justify-center">
          <div className="bg-white rounded-xl px-6 sm:px-8 md:px-10 lg:px-12 py-4 sm:py-6 md:py-8 shadow-md">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[4rem] font-bold uppercase tracking-[2px] sm:tracking-[3px] md:tracking-[4px] text-center text-[var(--text-dark)] font-sans m-0">
              OUR VALUES
            </h2>
          </div>
        </div>

        {/* Value Cards - responsive grid centered */}
        <div className="relative w-full max-w-[1400px] mt-4 sm:mt-6 md:mt-8 lg:mt-10 mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 z-[10]" data-about-grid>
          <div 
            className="bg-[var(--bg-pure-white)] p-6 sm:p-8 border-2 border-[#9a83ad] text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[200px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-base sm:text-lg mb-3 sm:mb-4 mt-0 flex-shrink-0 min-h-[2.5rem] flex items-center justify-center font-serif font-bold whitespace-nowrap">INTEGRITY ALWAYS</h3>
            <p className="text-sm sm:text-base leading-[1.6] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center">We uphold trust by staying true to our values, our word, and each other, even when it&apos;s hard, unseen, or unpopular.</p>
          </div>
          <div 
            className="bg-[var(--bg-pure-white)] p-6 sm:p-8 border-2 border-[#9a83ad] text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[200px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-base sm:text-lg mb-3 sm:mb-4 mt-0 flex-shrink-0 min-h-[2.5rem] flex items-center justify-center font-serif font-bold whitespace-nowrap">AMPLIFY WITH PURPOSE</h3>
            <p className="text-sm sm:text-base leading-[1.6] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center">We amplify voices—making them clearer, stronger, and more authentic.</p>
          </div>
          <div 
            className="bg-[var(--bg-pure-white)] p-6 sm:p-8 border-2 border-[#9a83ad] text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[200px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-base sm:text-lg mb-3 sm:mb-4 mt-0 flex-shrink-0 min-h-[2.5rem] flex items-center justify-center font-serif font-bold whitespace-nowrap">OWN THE OUTCOME</h3>
            <p className="text-sm sm:text-base leading-[1.6] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center">We own challenges and successes alike, always striving for solutions.</p>
          </div>
          <div 
            className="bg-[var(--bg-pure-white)] p-6 sm:p-8 border-2 border-[#9a83ad] text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[200px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-base sm:text-lg mb-3 sm:mb-4 mt-0 flex-shrink-0 min-h-[2.5rem] flex items-center justify-center font-serif font-bold whitespace-nowrap">BETTER TOGETHER</h3>
            <p className="text-sm sm:text-base leading-[1.6] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center">We treat everyone—team members, clients, and partners—with respect, knowing great work happens through collaboration and positivity.</p>
          </div>
          <div 
            className="bg-[var(--bg-pure-white)] p-6 sm:p-8 border-2 border-[#9a83ad] text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[200px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-base sm:text-lg mb-3 sm:mb-4 mt-0 flex-shrink-0 min-h-[2.5rem] flex items-center justify-center font-serif font-bold whitespace-nowrap">MAKE IT MAKE SENSE</h3>
            <p className="text-sm sm:text-base leading-[1.6] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center">We make things work, simply, clearly, and effectively.</p>
          </div>
        </div>
        
      </div>
    </section>
  );
};

export default About;
